import { useState } from 'react';
import { css } from '@emotion/react';
import { createUniqueClassName } from '../../../utils/createUniqueClassName';
import { Tooltip, TooltipPositionStyles, } from '../../display/Tooltip';
import { Animation } from '../../../theme';
import { useIntersect } from '../../../hooks';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { uniqueId } from '../../../utils/uniqueId';
const contentNodeClassName = createUniqueClassName('TooltipWrapper', 'content');
const contentNodeSelector = `.${contentNodeClassName}` + '[class]'.repeat(5);
const Styles = labelStyles('TooltipWrapper', {
    root: (props, correctedPosition) => [
        css `
      position: relative;
      display: inline-block;

      ${contentNodeSelector} {
        position: absolute;
        z-index: 1;
        ${correctedPosition ? TooltipPositionStyles[correctedPosition] : null};
        opacity: 0;
        transition: ${Animation.transitionValue};
        pointer-events: none;
      }

      // Edge doesnt understand ':focus-within' and will therefore
      // ignore the styles if theyre comboed like '&:hover, &:focus-within'.
      // As a workaround, separate the duplicated styles.
      &:hover ${contentNodeSelector} {
        opacity: 1;
        pointer-events: auto;
      }

      &:focus-within ${contentNodeSelector} {
        opacity: 1;
        pointer-events: auto;
      }
    `,
        !props.isTooltipInteractive &&
            css `
        ${contentNodeSelector}[class] {
          label: -non-interactive;
          pointer-events: none;
        }
      `,
        !props.showTooltipOnTouchDevices &&
            css `
        @media (hover: none) {
          ${contentNodeSelector}[class] {
            label: -hide-tooltip-on-touch-devices;
            opacity: 0;
          }
        }
      `,
        props.isBlock &&
            css `
        label: -block;
        display: block;
      `,
    ],
});
const correctPosition = (position, result) => {
    const { isTopIntersecting, isBottomIntersecting, isLeftIntersecting, isRightIntersecting, } = result;
    if (isBottomIntersecting && position.includes('south')) {
        return position.replace('south', 'north');
    }
    else if (isTopIntersecting && position.includes('north')) {
        return position.replace('north', 'south');
    }
    else if (isLeftIntersecting && position.includes('west')) {
        return position.replace('west', 'east');
    }
    else if (isRightIntersecting && position.includes('east')) {
        return position.replace('east', 'west');
    }
    return position;
};
const defaultProps = {
    position: 'south',
    shouldCorrectPosition: true,
    showTooltipOnTouchDevices: true,
    tooltipAriaHidden: true,
};
export const TooltipWrapper = (props) => {
    const { children, content, position } = Object.assign(Object.assign({}, defaultProps), props);
    const [tooltipId] = useState(props.tooltipId || uniqueId('tooltip-'));
    const shouldRenderTooltip = !!content;
    const childProps = {
        ariaDescribedBy: shouldRenderTooltip ? tooltipId : undefined,
    };
    const threshold = 0.9;
    const rootMargin = `-25px`;
    const [setNode, result] = useIntersect({
        threshold,
        rootMargin,
    });
    const correctedPosition = props.shouldCorrectPosition && result && Object.keys(result).length > 0
        ? correctPosition(position, result)
        : position;
    return (isolateComponent("div", { css: Styles.root(props, correctedPosition), ref: setNode },
        children({
            childProps,
            tooltipId,
        }),
        shouldRenderTooltip && (isolateComponent("div", { className: contentNodeClassName, "data-testid": `TooltipWrapper__content--${correctedPosition}` },
            isolateComponent(Tooltip, { allowMultiline: props.allowMultilineTooltip, ariaHidden: props.tooltipAriaHidden, id: tooltipId, isInteractive: true, position: null }, content)))));
};
TooltipWrapper.defaultProps = {
    position: 'south',
    shouldCorrectPosition: true,
    showTooltipOnTouchDevices: true,
    tooltipAriaHidden: true,
};
//# sourceMappingURL=TooltipWrapper.js.map
// @zapier/design-system@6.3.0 (1dc71580e)
